import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ProtocolData } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { CZK, spotLabel, TYPE_LABEL } from '../lib/format';
import { splitDt } from '../lib/filters';

/**
 * The handover protocol — the legacy "Prot." button, as a page laid out for
 * paper. Printed at the desk when a car is taken in and handed to the valet
 * with the key: who the customer is, what the car is, where it should go,
 * the KeyCafe codes, the services ordered, and four blank lines the driver
 * fills in by hand as the car moves. The layout follows the legacy sheet
 * (tpl/page.printprotocol.html) field for field — the people who read it
 * have read that one for years.
 *
 * It prints itself when it has loaded, which is what the legacy page did in
 * its popup; the two buttons are there for the second copy and for closing.
 * Nothing on this page writes anything.
 */
export function Protokol() {
  const { id } = useParams();
  const rid = Number(id);
  /* `?tisk=0` shows the sheet without printing it — a preview, for checking
     a sheet before the paper, and for anyone looking at it on a screen. */
  const [sp] = useSearchParams();
  const preview = sp.get('tisk') === '0';
  const q = useQuery({
    queryKey: ['protocol', rid],
    queryFn: () => api.protocol(rid),
    enabled: Number.isInteger(rid) && rid > 0,
    retry: false,
  });

  useEffect(() => {
    if (!q.data || preview) return;
    // Give the browser one frame to lay the sheet out before the print
    // dialog freezes it; printing a half-painted page prints a blank one.
    const t = window.setTimeout(() => window.print(), 250);
    return () => window.clearTimeout(t);
  }, [q.data, preview]);

  if (!Number.isInteger(rid) || rid <= 0) {
    return <div className="container state">Neplatné číslo rezervace.</div>;
  }
  if (q.isPending) return <div className="container state">Připravuji protokol…</div>;
  if (q.isError) {
    const notFound = q.error instanceof ApiError && q.error.status === 404;
    return (
      <div className="container state">
        {notFound ? `Rezervace ${rid} neexistuje.` : 'Protokol se nepodařilo načíst.'}
      </div>
    );
  }

  const { reservation: r, services } = q.data as ProtocolData;
  const arr = splitDt(r.arrival);
  const dep = splitDt(r.departure);
  const fill = <span className="fillbox">&nbsp;</span>;

  return (
    <div className="protokol">
      <div className="controls noprint">
        <button type="button" className="btn" onClick={() => window.print()}>
          {preview ? 'Tisk' : 'Opakovat tisk'}
        </button>
        <button type="button" className="btn" onClick={() => window.close()}>
          Zavřít okno
        </button>
      </div>

      <div className="sheet">
        <h1>{r.driverName || '—'}</h1>

        <div className="box grid2">
          <div>
            <h2>
              Rezervace ID: <strong>{r.id}</strong>
            </h2>
            <p>
              <strong>Předávající:</strong> {r.driverName || fill}
            </p>
            <p>
              <strong>Parkování:</strong> {arr.d} {arr.t} – {dep.d} {dep.t}
            </p>
            <p className={r.type === 'regular' ? 'box' : undefined}>
              <strong>Typ parkování:</strong> {TYPE_LABEL[r.type] ?? r.type}
            </p>
            <p>
              <strong>Typ vozidla:</strong> {r.car || fill}
            </p>
            <p>
              <strong>SPZ:</strong> {r.spz ? <span className="spz">{r.spz}</span> : fill}
            </p>
          </div>
          <div className="right">
            <p>
              <strong>Doporučené místo:</strong> {r.parkingTarget ? spotLabel(r.parkingTarget) : fill}
            </p>
            <p>
              <strong>Zaparkováno:</strong> {r.parking ? spotLabel(r.parking) : fill}
            </p>
            <p className="gap">
              <strong>Europort příjezd:</strong> {fill}
            </p>
            <p className="gap">
              <strong>Tuchoměřice:</strong> {fill}
            </p>
            <p>
              <strong>Parkoviště číslo klíčů:</strong> {fill}
            </p>
            <p className="gap">
              <strong>Europort odjezd:</strong> {fill}
            </p>
          </div>
        </div>

        <div className="grid2 keys">
          <div>
            <h3>
              KeyCafe klíče vyndat: <strong>{r.keycafeArrival || '—'}</strong>
            </h3>
            <h3>
              KeyCafe klíče vložit: <strong>{r.keycafeDeparture || '—'}</strong>
            </h3>
          </div>
          <div>
            <h3 className="center">Doplňkové služby</h3>
            <table>
              <tbody>
                {services.map((s) => (
                  <tr key={s.id} className={s.ordered ? 'ordered' : undefined}>
                    <th>{s.title}</th>
                    <td className="num">{CZK.format(s.price)} Kč</td>
                    <td className="tick">
                      <span className="checkbox">{s.ordered ? '✓' : ''}</span>
                      {s.ordered ? ' ANO' : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="box note">
          <h4>Poznámka:</h4>
          <p>{r.note}</p>
        </div>
      </div>
    </div>
  );
}
